import {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import {
  useForm,
  useFieldArray,
  useWatch,
  type FieldErrors,
} from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/custom/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import SelectInput from "@/components/select-input/select-input";
import type {
  CreateOperationalHoursDto,
  OperationalHoursResponseDto,
} from "@/client";
import { timezoneOptions } from "@/components/custom/timezone-options";

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

const scheduleEntrySchema = z.object({
  dayOfWeek: z.enum(DAYS_OF_WEEK),
  startTime: z.string().regex(TIME_REGEX, "invalidTimeFormat"),
  endTime: z.string().regex(TIME_REGEX, "invalidTimeFormat"),
  enabled: z.boolean(),
});

const operationalHoursSchema = z.object({
  enabled: z.boolean(),
  timezone: z.object({ value: z.string(), label: z.string() }),
  schedule: z.array(scheduleEntrySchema),
});

type FormValues = z.infer<typeof operationalHoursSchema>;

const DEFAULT_SCHEDULE = DAYS_OF_WEEK.map((day) => ({
  dayOfWeek: day,
  startTime: "08:00",
  endTime: "22:00",
  enabled: true,
}));

const DAY_TRANSLATION_KEYS = {
  Monday: "monday",
  Tuesday: "tuesday",
  Wednesday: "wednesday",
  Thursday: "thursday",
  Friday: "friday",
  Saturday: "saturday",
  Sunday: "sunday",
} as const satisfies Record<(typeof DAYS_OF_WEEK)[number], string>;

export interface OperationalHoursEditorRef {
  /** Resolves to true if validation passed and save was attempted, false if validation failed. */
  submit: () => Promise<boolean>;
  reset: () => void;
}

interface OperationalHoursEditorProps {
  value?: OperationalHoursResponseDto | null;
  onSave: (data: CreateOperationalHoursDto) => Promise<void>;
  /** Completely disables all interaction (no edit button shown). */
  disabled?: boolean;
  /**
   * When provided, the component operates in controlled mode:
   * internal Edit/Cancel/Save buttons are hidden and the edit state
   * is driven by this prop. Use together with a forwarded ref to
   * imperatively submit or reset.
   */
  isEditing?: boolean;
  /**
   * In internal (uncontrolled) mode, controls whether the Edit button
   * is rendered. Defaults to true. Has no effect in controlled mode.
   */
  editable?: boolean;
  /**
   * Called whenever the internal form dirty state changes.
   * Useful in controlled mode so the parent can enable/disable its own Save button.
   */
  onDirtyChange?: (isDirty: boolean) => void;
}

export const OperationalHoursEditor = forwardRef<
  OperationalHoursEditorRef,
  OperationalHoursEditorProps
>(
  (
    {
      value,
      onSave,
      disabled = false,
      isEditing: externalIsEditing,
      editable = true,
      onDirtyChange,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isControlled = externalIsEditing !== undefined;
    const [internalIsEditing, setInternalIsEditing] = useState(false);

    const isEditingActive = isControlled
      ? externalIsEditing!
      : internalIsEditing;

    const defaultTimezone = useMemo(() => {
      const tz =
        value?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      return { value: tz, label: tz.replace(/_/g, " ") };
    }, [value?.timezone]);

    const buildFormValues = () => ({
      enabled: value?.enabled ?? false,
      timezone: defaultTimezone,
      schedule: value?.schedule?.length
        ? DAYS_OF_WEEK.map((day) => {
            const existing = value.schedule.find((s) => s.dayOfWeek === day);
            return (
              existing || {
                dayOfWeek: day,
                startTime: "08:00",
                endTime: "22:00",
                enabled: false,
              }
            );
          })
        : DEFAULT_SCHEDULE,
    });

    const form = useForm<FormValues>({
      resolver: zodResolver(operationalHoursSchema),
      defaultValues: buildFormValues(),
    });

    const { fields } = useFieldArray({
      control: form.control,
      name: "schedule",
    });

    // Notify parent when dirty state changes (useful in controlled mode)
    useEffect(() => {
      onDirtyChange?.(form.formState.isDirty);
    }, [form.formState.isDirty, onDirtyChange]);

    // Reset form when value changes externally (only when not actively editing)
    useEffect(() => {
      if (!isEditingActive) {
        form.reset(buildFormValues());
      }
    }, [value]);

    // In controlled mode: reset form when the parent exits edit mode
    useEffect(() => {
      if (isControlled && !externalIsEditing) {
        form.reset(buildFormValues());
      }
    }, [externalIsEditing]);

    const handleSubmit = async (data: FormValues) => {
      await onSave({
        enabled: data.enabled,
        timezone: data.timezone.value,
        schedule: data.schedule,
      });
      if (!isControlled) {
        setInternalIsEditing(false);
      }
    };

    const handleInvalid = (errors: FieldErrors<FormValues>) => {
      console.warn("[OperationalHoursEditor] validation failed", errors);
      toast.error(t("operationalHoursValidationError"));
    };

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          let didSubmit = false;
          await form.handleSubmit(async (data) => {
            await handleSubmit(data);
            didSubmit = true;
          }, handleInvalid)();
          return didSubmit;
        },
        reset: () => {
          form.reset(buildFormValues());
        },
      }),
      [form, value, defaultTimezone],
    );

    const enabled = form.watch("enabled");
    const scheduleValues = useWatch({
      control: form.control,
      name: "schedule",
    });

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
          className="space-y-4"
        >
          <div className="flex items-center justify-between">
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex items-center gap-3">
                  <FormLabel className="text-base font-normal mt-2">
                    {t("enableOperationalHours")}
                  </FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        if (!isControlled && !internalIsEditing)
                          setInternalIsEditing(true);
                      }}
                      disabled={disabled || !isEditingActive}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {enabled && (
            <>
              <FormField
                control={form.control}
                name="timezone"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {t("timezone")}
                    </FormLabel>
                    <FormControl>
                      <SelectInput
                        {...field}
                        isMulti={false}
                        isClearable={false}
                        isSearchable={true}
                        options={timezoneOptions}
                        className="w-1/3"
                        isDisabled={disabled || !isEditingActive}
                        onChange={(val) => {
                          field.onChange(val);
                          if (!isControlled && !internalIsEditing)
                            setInternalIsEditing(true);
                        }}
                      />
                    </FormControl>
                    {fieldState.error && (
                      <p className="text-sm text-destructive">
                        {t("invalidTimezone")}
                      </p>
                    )}
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <div className="grid grid-cols-[140px_60px_1fr_1fr] gap-2 items-center text-sm font-medium text-muted-foreground">
                  <div>{t("day")}</div>
                  <div></div>
                  <div>{t("startTime")}</div>
                  <div>{t("endTime")}</div>
                </div>

                {fields.map((field, index) => {
                  const dayEnabled = scheduleValues[index]?.enabled ?? false;
                  return (
                    <div
                      key={field.id}
                      className="grid grid-cols-[140px_60px_1fr_1fr] gap-2 items-center"
                    >
                      <span className="text-sm">
                        {t(DAY_TRANSLATION_KEYS[field.dayOfWeek])}
                      </span>

                      <FormField
                        control={form.control}
                        name={`schedule.${index}.enabled`}
                        render={({ field: switchField }) => (
                          <FormControl>
                            <Switch
                              checked={switchField.value}
                              onCheckedChange={(checked) => {
                                switchField.onChange(checked);
                                if (!isControlled && !internalIsEditing)
                                  setInternalIsEditing(true);
                              }}
                              disabled={disabled || !isEditingActive}
                            />
                          </FormControl>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`schedule.${index}.startTime`}
                        render={({ field: timeField, fieldState }) => (
                          <div>
                            <FormControl>
                              <Input
                                {...timeField}
                                type="time"
                                className="w-32"
                                disabled={
                                  disabled || !isEditingActive || !dayEnabled
                                }
                                onChange={(e) => {
                                  timeField.onChange(e);
                                  if (!isControlled && !internalIsEditing)
                                    setInternalIsEditing(true);
                                }}
                              />
                            </FormControl>
                            {fieldState.error && (
                              <p className="text-xs text-destructive mt-0.5">
                                {t("invalidTimeFormat")}
                              </p>
                            )}
                          </div>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`schedule.${index}.endTime`}
                        render={({ field: timeField, fieldState }) => (
                          <div>
                            <FormControl>
                              <Input
                                {...timeField}
                                type="time"
                                className="w-32"
                                disabled={
                                  disabled || !isEditingActive || !dayEnabled
                                }
                                onChange={(e) => {
                                  timeField.onChange(e);
                                  if (!isControlled && !internalIsEditing)
                                    setInternalIsEditing(true);
                                }}
                              />
                            </FormControl>
                            {fieldState.error && (
                              <p className="text-xs text-destructive mt-0.5">
                                {t("invalidTimeFormat")}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {!isControlled && !disabled && (
            <div className="flex gap-2 pt-2">
              {!internalIsEditing ? (
                editable && (
                  <Button
                    type="button"
                    variant="azure"
                    onClick={() => setInternalIsEditing(true)}
                  >
                    {t("edit")}
                  </Button>
                )
              ) : (
                <>
                  <Button
                    type="button"
                    variant="azure"
                    onClick={() => {
                      form.reset(buildFormValues());
                      setInternalIsEditing(false);
                    }}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    type="submit"
                    variant="azure"
                    disabled={form.formState.isSubmitting}
                  >
                    {form.formState.isSubmitting ? t("saving") : t("save")}
                  </Button>
                </>
              )}
            </div>
          )}
        </form>
      </Form>
    );
  },
);
